// Lightweight Google Analytics 4 loader.
// Set REACT_APP_GA_ID in /app/frontend/.env (e.g. G-XXXXXXXXXX) to enable.
// Also allows a runtime override via `window.__GA_ID` for platform injection.

const GA_ID = process.env.REACT_APP_GA_ID || (typeof window !== 'undefined' && window.__GA_ID) || '';

let injected = false;

export function initAnalytics() {
  if (injected) return;
  if (!GA_ID) return; // no-op until an ID is configured
  injected = true;

  const s1 = document.createElement('script');
  s1.async = true;
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s1);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID, {
    anonymize_ip: true,
    send_page_view: false, // we send manually per route
  });
}

// ---------- first-party, anonymous tracking (own Traffic dashboard) ----------
const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_TTL_MS = 30 * 60 * 1000;

function uid() {
  return (window.crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`).replace(/-/g, '');
}

function visitorId() {
  try {
    let v = localStorage.getItem('rl_vid');
    if (!v) { v = uid(); localStorage.setItem('rl_vid', v); }
    return v;
  } catch (_) { return 'anon' + uid(); }
}

function sessionId() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem('rl_sid');
    if (raw) {
      const [sid, last] = raw.split('|');
      if (now - Number(last) < SESSION_TTL_MS) { sessionStorage.setItem('rl_sid', `${sid}|${now}`); return sid; }
    }
    const sid = uid();
    sessionStorage.setItem('rl_sid', `${sid}|${now}`);
    return sid;
  } catch (_) { return 'sess' + uid(); }
}

function utmParams() {
  try {
    const q = new URLSearchParams(window.location.search);
    const utm = { source: q.get('utm_source'), medium: q.get('utm_medium'), campaign: q.get('utm_campaign') };
    if (utm.source) { sessionStorage.setItem('rl_utm', JSON.stringify(utm)); return utm; }
    const saved = sessionStorage.getItem('rl_utm');
    return saved ? JSON.parse(saved) : null;
  } catch (_) { return null; }
}

let lastKey = '';
let lastAt = 0;

function beacon(payload) {
  if (!API || typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/admin')) return;
  // drop exact duplicates fired within 1.5s (React StrictMode double effects, double clicks)
  const key = `${payload.type}|${payload.name || ''}|${payload.path}|${payload.product_id || ''}`;
  const now = Date.now();
  if (key === lastKey && now - lastAt < 1500) return;
  lastKey = key; lastAt = now;
  const body = JSON.stringify({ ...payload, vid: visitorId(), sid: sessionId(), utm: utmParams(), referrer: document.referrer || null, screen_w: window.innerWidth, lang: navigator.language });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/api/track`, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(`${API}/api/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    }
  } catch (_) { /* never break the UI for analytics */ }
}

export function trackPageView(path, title) {
  beacon({ type: 'pageview', path, title: title || document.title });
  if (!GA_ID || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

export function trackEvent(name, params = {}) {
  beacon({ type: 'event', name, path: window.location.pathname, product_id: params.item_id || params.product_id || null, slug: params.slug || null,
    value: params.value != null ? Number(params.value) : null, quantity: params.quantity != null ? Number(params.quantity) : null, order_id: params.transaction_id || null });
  if (!GA_ID || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}

export const isAnalyticsEnabled = () => Boolean(GA_ID);
